import { booleanAttribute, Component, computed, inject, input, output, viewChild } from '@angular/core';
import { UIIconComponent, UiButtonComponent, UiNumberComponent, UiProgressBarComponent } from '@design-system';
import { EDigitPipe, JdatePipe } from '@tools';
import { BottomSheetComponent } from '../../../../components/bottom-sheet/bottom-sheet.component';
import { OrderFormProducerService } from '../../../../events/order-form/order-form-producer.service';
import {
	getOrderStatusLabel,
	getRemainingQuantity,
	getTradeRows,
	isOpenOrder,
	Order,
	toOrderFormInput
} from '../../../../models/order.model';
import { OrderSideEnum, OrderStateEnum } from '../../../../models/order-response.model';
import { ORDER_ERROR_CODES } from '../../../../constants/order-error-code';

/** The order's symbol as the market quotes it right now. */
export interface OrderDetailLastPrice {
	price: number;
	change: number;
	changePercent: number;
}

@Component({
	selector: 'app-order-detail-bottom-sheet',
	imports: [
		BottomSheetComponent,
		UIIconComponent,
		UiButtonComponent,
		UiNumberComponent,
		UiProgressBarComponent,
		JdatePipe,
		EDigitPipe
	],
	templateUrl: './order-detail-bottom-sheet.component.html',
	host: {
		'data-cy': 'order-detail-bottom-sheet'
	}
})
export class OrderDetailBottomSheetComponent {
	private readonly orderFormProducer = inject(OrderFormProducerService);

	readonly order = input<Order | null>(null);

	readonly showActions = input(true, { transform: booleanAttribute });
	/** Supplied by the open-orders page only; the row stays hidden while null (e.g. order history). */
	readonly lastPrice = input<OrderDetailLastPrice | null>(null);

	/** Asks the page for its delete confirmation, which the page raises in place of this sheet. */
	readonly cancelRequested = output<void>();

	private readonly sheet = viewChild<BottomSheetComponent>('sheet');

	readonly OrderSide = OrderSideEnum;

	/** Lets the page keep the sheet's order and fills fresh only while it is actually on screen. */
	readonly isOpen = computed(() => this.sheet()?.isOpen() ?? false);

	readonly quantity = computed(() => this.order()?.volume ?? 0);
	readonly price = computed(() => this.order()?.price ?? 0);

	readonly lastPriceUp = computed(() => (this.lastPrice()?.change ?? 0) >= 0);

	readonly orderState = computed(() => this.order()?.state);

	readonly isErrorState = computed(() => this.orderState() === OrderStateEnum.Error);

	readonly errorDescription = computed(() => {
		const state = this.orderState();
		if (state && this.isErrorState()) return `خطا${this.getErrorMessage(this.order()?.errorCode ?? null)}`;
		return null;
	});

	getErrorMessage(errorCode: string | null) {
		if (!errorCode) return '';
		return ORDER_ERROR_CODES[errorCode] ? `: ${ORDER_ERROR_CODES[errorCode]}` : '';
	}

	readonly statusLabel = computed(() => {
		const order = this.order();
		if (this.isErrorState()) return this.errorDescription();
		return order ? getOrderStatusLabel(order) : '';
	});

	readonly remainingQuantity = computed(() => {
		const order = this.order();
		return order ? getRemainingQuantity(order) : 0;
	});

	readonly trades = computed(() => this.order()?.trades ?? []);

	/** Each execution with its segment position on the shared progress track and its rial value. */
	readonly tradeRows = computed(() => {
		const order = this.order();
		return order ? getTradeRows(order) : [];
	});

	/** Edit and cancel are only offered while the order is still open. */
	readonly canEditOrCancel = computed(() => {
		const order = this.order();
		return order ? isOpenOrder(order) : false;
	});

	open(): Promise<void> {
		return this.sheet()?.open() ?? Promise.resolve();
	}

	close(): Promise<void> {
		return this.sheet()?.close() ?? Promise.resolve();
	}

	/**
	 * The sheet is closed before the form is raised, so the close animation finishes
	 * and this sheet leaves the sheet queue instead of the form waiting behind it.
	 */
	async onCopyClick(): Promise<void> {
		const order = this.order();
		if (!order) {
			return;
		}
		await this.close();
		this.orderFormProducer.openOrderForm(toOrderFormInput(order, { isEdit: false, isCopy: true }));
	}

	async onEditClick(): Promise<void> {
		const order = this.order();
		if (!order) {
			return;
		}
		await this.close();
		this.orderFormProducer.openOrderForm(toOrderFormInput(order, { isEdit: true, isCopy: false }));
	}

	/**
	 * The confirmation is a sheet of its own, hosted by the page: the page closes this sheet
	 * before raising it, so the two are never stacked, and brings this one back if the user
	 * changes their mind.
	 */
	onCancelClick(): void {
		this.cancelRequested.emit();
	}
}
